import { type FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex font-body-md">
      {/* Left panel — full-bleed photo, hidden on mobile */}
      <div className="hidden md:flex md:w-[55%] relative flex-col">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=1200&auto=format&fit=crop&q=80)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-espresso/80 via-espresso/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <span className="font-display-hero text-[22px] tracking-tighter text-cream uppercase">REPORTER</span>
          <div className="flex flex-col gap-4 max-w-sm">
            <h2 className="font-display-hero text-[36px] text-cream leading-tight">Your stories deserve a magazine.</h2>
            <p className="font-body-md text-[15px] text-sand">50 world-class editorial themes. Africa-first publishing.</p>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full md:w-[45%] bg-espresso text-cream flex flex-col items-center justify-center px-8 py-12 selection:bg-terracotta selection:text-cream">
        <header className="flex flex-col items-center gap-2 mb-10">
          <span className="material-symbols-outlined text-terracotta text-4xl md:hidden">menu_book</span>
          <h1 className="font-display-hero text-[28px] text-cream tracking-tighter uppercase md:hidden">REPORTER</h1>
          <h1 className="font-display-hero text-[28px] text-cream tracking-tighter uppercase hidden md:block">Welcome back</h1>
          <p className="font-meta-code text-[11px] text-fog uppercase tracking-widest">Back in the field.</p>
        </header>

        <main className="w-full max-w-sm bg-bark border border-ash p-8 flex flex-col gap-6">
          <div>
            <h2 className="font-headline-md text-[22px] text-cream leading-tight">Sign in</h2>
            <p className="font-meta-code text-meta-code text-fog mt-1">Your stories are waiting.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="font-label-caps text-label-caps text-fog uppercase">Email</label>
              <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} placeholder="your@email.com" className="input-underline-safari font-body-md text-cream bg-transparent" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="font-label-caps text-label-caps text-fog uppercase">Password</label>
              <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} placeholder="••••••••" className="input-underline-safari font-body-md text-cream bg-transparent" />
              <a href="#" className="font-meta-code text-[11px] text-terracotta self-end mt-1 hover:opacity-80 transition-opacity">Forgot password?</a>
            </div>
            {error && (
              <p role="alert" className="font-meta-code text-meta-code text-[#ffb4ab] border border-[#ffb4ab]/30 bg-[#ffb4ab]/10 px-3 py-2">{error}</p>
            )}
            <button type="submit" disabled={loading || !email || !password} className="w-full bg-terracotta text-cream font-label-caps text-label-caps uppercase py-4 hover:bg-dusk transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed mt-1" style={{ borderRadius: 0 }}>
              {loading ? 'SIGNING IN…' : 'SIGN IN'}
            </button>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-ash" />
              <span className="font-meta-code text-[10px] text-fog uppercase tracking-wider">or continue with</span>
              <div className="flex-1 h-px bg-ash" />
            </div>
            <button type="button" disabled title="Google sign-in coming soon" className="w-full flex items-center justify-center gap-3 border border-ash py-3 font-meta-code text-meta-code text-fog opacity-50 cursor-not-allowed" style={{ borderRadius: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </button>
          </form>
        </main>

        <p className="font-meta-code text-meta-code text-fog mt-6">
          New to Reporter?{' '}
          <Link to="/register" state={{ from: { pathname: from } }} className="text-cream hover:text-terracotta transition-colors">Publish your first story free →</Link>
        </p>
      </div>
    </div>
  );
}
